/**
 * Jupiter Ultra API integration for dust swapping
 *
 * This module handles fetching swap quotes and executing swaps via Jupiter Ultra API.
 * Only works on mainnet - Jupiter doesn't support devnet.
 *
 * API Key: Required. Get a free key (60 req/min) from https://portal.jup.ag
 * Set via VITE_JUPITER_API_KEY environment variable.
 *
 * Migration note: Updated from api.jup.ag/swap/v1 to api.jup.ag/ultra/v1
 * Ultra provides: faster quotes (single API call), better routing (Juno engine),
 * faster landing (~200ms via Jupiter Beam), and gasless swaps when available.
 * See: https://dev.jup.ag/docs/ultra
 */

import {
  Connection,
  VersionedTransaction,
  TransactionMessage,
  AddressLookupTableAccount,
  PublicKey,
} from '@solana/web3.js';

import config from '@/config';
import { getSwapUserInfoPDA } from './anchorClient';

// Jupiter Ultra API endpoint
// See: https://dev.jup.ag/docs/ultra
const JUPITER_ULTRA_API_URL = 'https://api.jup.ag/ultra/v1';

// Jupiter Legacy API endpoint (swap/v1)
// See: https://dev.jup.ag/docs/swap
const JUPITER_LEGACY_API_URL = 'https://api.jup.ag';

// API key from config (get free key from portal.jup.ag)
const getJupiterApiKey = (): string | undefined => {
  return (config as any).JUPITER_API_KEY;
};

// Get Jupiter API version from config ('ultra' or 'legacy')
const getJupiterApiVersion = (): 'ultra' | 'legacy' => {
  return (config as any).JUPITER_API_VERSION || 'ultra';
};

// SOL mint address (output token for all swaps)
const SOL_MINT = 'So11111111111111111111111111111111111111112';

/**
 * Jupiter error types from the API
 * @see https://dev.jup.ag/docs/swap/common-errors
 */
export enum JupiterErrorType {
  // Route errors
  COULD_NOT_FIND_ANY_ROUTE = 'Could not find any route',
  ROUTE_PLAN_DOES_NOT_CONSUME_ALL_THE_AMOUNT = 'Route plan does not consume all the amount',
  NO_ROUTES_FOUND = 'No routes found',
  MARKET_NOT_FOUND = 'Market not found',
  TOKEN_NOT_TRADABLE = 'Token not tradable',
  NOT_SUPPORTED = 'Not supported',
  CIRCULAR_ARBITRAGE_IS_DISABLED = 'Circular arbitrage is disabled',
  CANNOT_COMPUTE_OTHER_AMOUNT_THRESHOLD = 'Cannot compute other amount threshold',
  // Compose errors
  MAX_ACCOUNT_GREATER_THAN_MAX = 'Max account greater than max',
  INVALID_COMPUTE_UNIT_PRICE_AND_PRIORITIZATION_FEE = 'Invalid compute unit price and prioritization fee',
  FAILED_TO_GET_SWAP_AND_ACCOUNT_METAS = 'Failed to get swap and account metas',
  // Transaction size errors (from backend when building swap tx)
  VERSIONED_TRANSACTION_TOO_LARGE = 'Versioned Transaction Too Large',
  TRANSACTION_TOO_LARGE = 'Transaction too large',
}

/**
 * Jupiter quote response from the API
 */
export interface JupiterQuoteResponse {
  inputMint: string;
  inAmount: string;
  outputMint: string;
  outAmount: string;
  otherAmountThreshold: string;
  swapMode: string;
  slippageBps: number;
  priceImpactPct: string;
  routePlan: Array<{
    swapInfo: {
      ammKey: string;
      label?: string;
      inputMint: string;
      outputMint: string;
      inAmount: string;
      outAmount: string;
      feeAmount: string;
      feeMint: string;
    };
    percent: number;
  }>;
  contextSlot?: number;
  timeTaken?: number;
}

/**
 * Serialized instruction format for backend
 */
export interface SerializedInstruction {
  programId: string;
  keys: Array<{
    pubkey: string;
    isSigner: boolean;
    isWritable: boolean;
  }>;
  data: string; // base64 encoded
}

/**
 * Jupiter swap instructions response from the API
 * Extended for Ultra API compatibility
 */
export interface JupiterSwapInstructionsResponse {
  tokenLedgerInstruction?: any;
  computeBudgetInstructions: any[];
  setupInstructions: any[];
  swapInstruction: any;
  cleanupInstruction?: any;
  otherInstructions: any[];
  addressLookupTableAddresses: string[];
  prioritizationFeeLamports?: number;
  computeUnitLimit?: number;
  prioritizationType?: {
    computeBudget?: {
      microLamports?: number;
      estimatedMicroLamports?: number;
    };
  };
  dynamicSlippageReport?: any;
  simulationError?: any;

  // Ultra API extension - pre-serialized instructions extracted from Ultra transaction
  _ultraExtractedInstructions?: SerializedInstruction[];
}

/**
 * Jupiter Ultra Order Response
 * Response from GET /ultra/v1/order endpoint
 */
export interface JupiterUltraOrderResponse {
  requestId: string;
  inputMint: string;
  outputMint: string;
  inAmount: string;
  outAmount: string;
  otherAmountThreshold: string;
  swapMode: string;
  slippageBps: number;
  priceImpactPct: string;
  routePlan: Array<{
    swapInfo: {
      ammKey: string;
      label?: string;
      inputMint: string;
      outputMint: string;
      inAmount: string;
      outAmount: string;
      feeAmount: string;
      feeMint: string;
    };
    percent: number;
  }>;
  contextSlot?: number;
  timeTaken?: number;
  swapType?: string;
  // Transaction is base64-encoded, null if no taker provided (price-only quote)
  transaction: string | null;
  // Gasless swap info
  gasless?: boolean;
  // Error info
  errorCode?: string;
  error?: string;
}

/**
 * Result for a single token's Jupiter quote attempt
 */
export interface JupiterQuoteResult {
  mint: string;
  tokenAccount: string;
  balance: number;
  decimals: number;
  symbol: string;
  quote: JupiterQuoteResponse | null;
  swapInstructions: JupiterSwapInstructionsResponse | null;
  addressLookupTableAddresses: string[];
  error?: string;
  canRetry: boolean;
}

/**
 * Categorized swap results
 */
export interface SwapCategories {
  /** Tokens that have valid quotes and can be swapped */
  swappable: JupiterQuoteResult[];
  /** Tokens that failed due to rate limiting or temporary issues - user should retry */
  retryable: JupiterQuoteResult[];
  /** Tokens that cannot be swapped (no route, not tradable, etc.) - can be burned instead */
  nonSwappable: JupiterQuoteResult[];
}

/**
 * Input token type for swap operations
 */
export interface SwappableToken {
  mint: string;
  tokenAccount: string;
  balance: number;
  decimals: number;
  symbol: string;
  /** Live rent deposit of the token account (returned to the user when it is closed) */
  lamports?: number;
}

/**
 * Parse Jupiter API error and determine if it's retryable
 */
async function handleJupiterError(error: any): Promise<{ error: string; canRetry: boolean }> {
  // Check for Response object from fetch
  if (error instanceof Response) {
    const status = error.status;

    // Rate limit - should retry
    if (status === 429) {
      return {
        error: 'Rate limit hit, please try again later.',
        canRetry: true,
      };
    }

    try {
      const responseText = await error.text();

      // Check for known Jupiter errors
      for (const errorType of Object.values(JupiterErrorType)) {
        if (responseText.includes(errorType)) {
          return {
            error: errorType,
            canRetry: false,
          };
        }
      }

      // Unknown error from response
      return {
        error: responseText || error.statusText || `HTTP ${status}`,
        canRetry: status >= 500, // Retry on server errors
      };
    } catch {
      return {
        error: error.statusText || `HTTP ${status}`,
        canRetry: status >= 500,
      };
    }
  }

  // Check for error object with response property
  if (error?.response) {
    const body = error.response.body;

    if (!body) {
      return {
        error: error.response.statusText || error.message || 'Unknown error',
        canRetry: false,
      };
    }

    let responseText = '';

    try {
      if (body instanceof ReadableStream) {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let temp = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          temp += decoder.decode(value, { stream: true });
        }
        responseText = temp;
      } else if (typeof body === 'string') {
        return {
          error: body,
          canRetry: false,
        };
      }
    } catch {
      return {
        error: error.message || 'Unknown error',
        canRetry: false,
      };
    }

    if (!responseText) {
      return {
        error: error.response.statusText || error.message || 'Unknown error',
        canRetry: false,
      };
    }

    // Rate limit hit
    if (responseText.includes('429') || responseText.toLowerCase().includes('rate limit')) {
      return {
        error: 'Rate limit hit, please try again later.',
        canRetry: true,
      };
    }

    // Check for known Jupiter errors
    for (const errorType of Object.values(JupiterErrorType)) {
      if (responseText.includes(errorType)) {
        return {
          error: errorType,
          canRetry: false,
        };
      }
    }

    return {
      error: responseText,
      canRetry: false,
    };
  }

  // Standard error
  const errorMessage = error?.message || String(error);

  // Network errors are retryable
  if (errorMessage.includes('fetch') ||
      errorMessage.includes('network') ||
      errorMessage.includes('timeout')) {
    return {
      error: errorMessage,
      canRetry: true,
    };
  }

  return {
    error: errorMessage,
    canRetry: false,
  };
}

/**
 * Get order from Jupiter Ultra for swapping a token to SOL
 * This replaces the old getQuote + getSwapInstructions two-step flow with a single API call.
 *
 * @param inputMint - Token mint address to swap from
 * @param amount - Amount in raw units (smallest denomination)
 * @param taker - Optional user wallet address. If provided, returns a transaction. If omitted, returns price-only quote.
 * @returns Ultra order response with quote data and optionally a transaction
 */
async function getUltraOrder(
  inputMint: string,
  amount: number,
  taker?: string,
  receiver?: string
): Promise<JupiterUltraOrderResponse> {
  const params = new URLSearchParams({
    inputMint,
    outputMint: SOL_MINT,
    amount: amount.toString(),
  });

  // Only add taker if provided (price-only quotes don't need it)
  if (taker) {
    params.append('taker', taker);
  }

  // Add receiver to route SOL output to the user_info PDA instead of taker's wallet
  // This is required for the swap program's fee collection mechanism to work
  if (receiver) {
    params.append('receiver', receiver);
  }

  // Limit accounts to prevent "Versioned Transaction Too Large" errors
  // We need room for our initSwapOrder + wrapUpSwapOrder instructions
  // Jupiter's default is 64, we reduce to 54 to leave headroom for our instructions
  params.append('maxAccounts', '54');

  const fullUrl = `${JUPITER_ULTRA_API_URL}/order?${params.toString()}`;
  console.log(`[JUPITER-ULTRA] Fetching order from: ${fullUrl}`);
  console.log(`[JUPITER-ULTRA] Request params:`, {
    inputMint,
    outputMint: SOL_MINT,
    amount,
    taker: taker || '(price-only)',
    receiver: receiver || '(default to taker)',
  });

  // Build headers with optional API key
  const headers: Record<string, string> = {};
  const apiKey = getJupiterApiKey();
  if (apiKey) {
    headers['x-api-key'] = apiKey;
    console.log(`[JUPITER-ULTRA] Using API key: ${apiKey.slice(0, 8)}...`);
  } else {
    console.warn(`[JUPITER-ULTRA] No API key configured - requests may be rate limited`);
  }

  let response: Response;
  try {
    response = await fetch(fullUrl, { headers });
    console.log(`[JUPITER-ULTRA] Response status: ${response.status} ${response.statusText}`);
  } catch (fetchError: any) {
    console.error(`[JUPITER-ULTRA] Fetch failed with error:`, fetchError);
    throw fetchError;
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unable to read error body');
    console.error(`[JUPITER-ULTRA] Response not OK. Body:`, errorText);
    throw response;
  }

  const json: JupiterUltraOrderResponse = await response.json();

  // Check for API-level errors in the response
  if (json.errorCode || json.error) {
    console.error(`[JUPITER-ULTRA] API error:`, json.error || json.errorCode);
    throw new Error(json.error || `Jupiter Ultra error: ${json.errorCode}`);
  }

  console.log(`[JUPITER-ULTRA] Order received successfully:`, {
    outAmount: json.outAmount,
    priceImpactPct: json.priceImpactPct,
    routePlanLength: json.routePlan?.length,
    hasTransaction: !!json.transaction,
    gasless: json.gasless,
    swapType: json.swapType,
  });

  return json;
}

/**
 * Extract and serialize instructions from a Jupiter Ultra transaction
 *
 * Ultra returns a complete base64-encoded VersionedTransaction. We need to:
 * 1. Deserialize it
 * 2. Fetch Address Lookup Table accounts for decompilation
 * 3. Decompile to access individual instructions
 * 4. Filter out compute budget instructions
 * 5. Serialize to our backend format
 *
 * @param base64Transaction - Base64-encoded transaction from Ultra API
 * @param connection - Solana connection for fetching ALT accounts
 * @returns Serialized instructions and ALT addresses for backend
 */
async function extractInstructionsFromUltraTransaction(
  base64Transaction: string,
  connection: Connection
): Promise<{
  instructions: SerializedInstruction[];
  addressLookupTableAddresses: string[];
}> {
  console.log(`[JUPITER-ULTRA] Extracting instructions from Ultra transaction...`);

  // Deserialize the transaction
  const txBuffer = Buffer.from(base64Transaction, 'base64');
  const transaction = VersionedTransaction.deserialize(txBuffer);

  // Get ALT addresses from transaction
  const altAddresses = transaction.message.addressTableLookups.map(
    (lookup) => lookup.accountKey.toString()
  );
  console.log(`[JUPITER-ULTRA] Transaction has ${altAddresses.length} Address Lookup Tables`);

  // Fetch ALT accounts for decompilation
  const altAccounts: AddressLookupTableAccount[] = [];
  if (transaction.message.addressTableLookups.length > 0) {
    const altKeys = transaction.message.addressTableLookups.map((l) => l.accountKey);
    const accountInfos = await connection.getMultipleAccountsInfo(altKeys);

    for (let i = 0; i < accountInfos.length; i++) {
      const info = accountInfos[i];
      if (info) {
        altAccounts.push(
          new AddressLookupTableAccount({
            key: altKeys[i],
            state: AddressLookupTableAccount.deserialize(info.data),
          })
        );
      }
    }
    console.log(`[JUPITER-ULTRA] Fetched ${altAccounts.length} ALT accounts`);
  }

  // Decompile the message to get instructions
  const message = TransactionMessage.decompile(transaction.message, {
    addressLookupTableAccounts: altAccounts,
  });

  console.log(`[JUPITER-ULTRA] Transaction has ${message.instructions.length} instructions`);

  // Filter out ComputeBudget program instructions and serialize the rest
  const COMPUTE_BUDGET_PROGRAM_ID = 'ComputeBudget111111111111111111111111111111';
  const swapInstructions = message.instructions.filter((ix) => {
    const programId = ix.programId.toString();
    return programId !== COMPUTE_BUDGET_PROGRAM_ID;
  });

  console.log(`[JUPITER-ULTRA] After filtering compute budget: ${swapInstructions.length} instructions`);

  // Serialize instructions to backend format
  const serializedInstructions: SerializedInstruction[] = swapInstructions.map((ix) => ({
    programId: ix.programId.toString(),
    keys: ix.keys.map((key) => ({
      pubkey: key.pubkey.toString(),
      isSigner: key.isSigner,
      isWritable: key.isWritable,
    })),
    data: Buffer.from(ix.data).toString('base64'),
  }));

  console.log(`[JUPITER-ULTRA] Serialized ${serializedInstructions.length} instructions for backend`);

  return {
    instructions: serializedInstructions,
    addressLookupTableAddresses: altAddresses,
  };
}

/**
 * Get Jupiter quotes for multiple tokens with swap instructions
 *
 * Supports both Ultra API (default, faster) and Legacy API (2-step process).
 * API version is determined by JUPITER_API_VERSION config.
 *
 * @param tokens - Array of tokens to get quotes for
 * @param userPublicKey - User's wallet address (taker for Ultra transactions)
 * @param connection - Solana connection for ALT resolution
 * @returns Categorized results (swappable, retryable, nonSwappable)
 */
export async function getJupiterQuotes(
  tokens: SwappableToken[],
  userPublicKey: string,
  connection: Connection
): Promise<SwapCategories> {
  const apiVersion = getJupiterApiVersion();
  console.log(`[JUPITER] Using ${apiVersion.toUpperCase()} API for quotes`);

  // Derive user_info PDA for the swap program - SOL output will be sent here
  // This allows the wrapUpSwapOrder instruction to calculate and collect the 10% fee
  const [userInfoPda] = getSwapUserInfoPDA(new PublicKey(userPublicKey));
  const receiverAddress = userInfoPda.toString();

  // Use Legacy API if configured
  if (apiVersion === 'legacy') {
    return getLegacyQuotes(tokens, userPublicKey, receiverAddress);
  }

  // Ultra API (default)
  console.log(`[JUPITER-ULTRA] Using receiver (user_info PDA): ${receiverAddress}`);

  const results = await Promise.all(
    tokens.map(async (token): Promise<JupiterQuoteResult> => {
      try {
        // Get Ultra order with transaction
        // - taker: user's wallet (pays for the swap)
        // - receiver: user_info PDA (receives SOL output for fee collection)
        const order = await getUltraOrder(token.mint, token.balance, userPublicKey, receiverAddress);

        // Ultra should return transaction if taker is provided
        if (!order.transaction) {
          throw new Error('No transaction returned from Jupiter Ultra API');
        }

        // Extract instructions from the Ultra transaction
        const { instructions, addressLookupTableAddresses } =
          await extractInstructionsFromUltraTransaction(order.transaction, connection);

        // Build quote response for compatibility with existing code
        const quote: JupiterQuoteResponse = {
          inputMint: order.inputMint,
          inAmount: order.inAmount,
          outputMint: order.outputMint,
          outAmount: order.outAmount,
          otherAmountThreshold: order.otherAmountThreshold,
          swapMode: order.swapMode,
          slippageBps: order.slippageBps,
          priceImpactPct: order.priceImpactPct,
          routePlan: order.routePlan,
          contextSlot: order.contextSlot,
          timeTaken: order.timeTaken,
        };

        // Build swap instructions response for compatibility
        // Store the extracted serialized instructions in the _ultraExtractedInstructions field
        const swapInstructions: JupiterSwapInstructionsResponse = {
          setupInstructions: [],
          swapInstruction: null,
          cleanupInstruction: null,
          computeBudgetInstructions: [],
          otherInstructions: [],
          addressLookupTableAddresses,
          _ultraExtractedInstructions: instructions,
        };

        return {
          mint: token.mint,
          tokenAccount: token.tokenAccount,
          balance: token.balance,
          decimals: token.decimals,
          symbol: token.symbol,
          quote,
          swapInstructions,
          addressLookupTableAddresses,
          canRetry: false,
        };
      } catch (error) {
        const { error: errorMessage, canRetry } = await handleJupiterError(error);

        console.warn(`[JUPITER-ULTRA] Failed to get order for ${token.symbol} (${token.mint.slice(0, 8)}...): ${errorMessage}`);

        return {
          mint: token.mint,
          tokenAccount: token.tokenAccount,
          balance: token.balance,
          decimals: token.decimals,
          symbol: token.symbol,
          quote: null,
          swapInstructions: null,
          addressLookupTableAddresses: [],
          error: errorMessage,
          canRetry,
        };
      }
    })
  );

  // Categorize results
  const swappable: JupiterQuoteResult[] = [];
  const retryable: JupiterQuoteResult[] = [];
  const nonSwappable: JupiterQuoteResult[] = [];

  for (const result of results) {
    if (result.quote && result.swapInstructions) {
      swappable.push(result);
    } else if (result.canRetry) {
      retryable.push(result);
    } else {
      nonSwappable.push(result);
    }
  }

  console.log(`[JUPITER-ULTRA] Quote results: ${swappable.length} swappable, ${retryable.length} retryable, ${nonSwappable.length} non-swappable`);

  return { swappable, retryable, nonSwappable };
}

/**
 * Serialize Jupiter instructions for backend
 * Converts the instruction format to JSON-serializable format
 *
 * For Ultra API: Uses pre-extracted instructions stored in _ultraExtractedInstructions
 * For legacy v1 API: Extracts from setupInstructions, swapInstruction, cleanupInstruction
 */
export function serializeInstructions(
  swapInstructions: JupiterSwapInstructionsResponse
): SerializedInstruction[] {
  // For Ultra API, use the pre-extracted instructions
  if (swapInstructions._ultraExtractedInstructions) {
    return swapInstructions._ultraExtractedInstructions;
  }

  // Legacy v1 API path (fallback, kept for compatibility)
  const instructions: SerializedInstruction[] = [];

  // Add setup instructions
  for (const ix of swapInstructions.setupInstructions || []) {
    instructions.push(serializeInstruction(ix));
  }

  // Add swap instruction
  if (swapInstructions.swapInstruction) {
    instructions.push(serializeInstruction(swapInstructions.swapInstruction));
  }

  // Add cleanup instruction
  if (swapInstructions.cleanupInstruction) {
    instructions.push(serializeInstruction(swapInstructions.cleanupInstruction));
  }

  return instructions;
}

/**
 * Serialize a single instruction (legacy v1 API format)
 */
function serializeInstruction(instruction: any): SerializedInstruction {
  return {
    programId: instruction.programId,
    keys: instruction.accounts.map((key: any) => ({
      pubkey: key.pubkey,
      isSigner: key.isSigner,
      isWritable: key.isWritable,
    })),
    data: instruction.data, // Already base64 encoded from Jupiter API
  };
}

/**
 * Batch Jupiter quote results for transaction building
 * Max 1-2 tokens per swap transaction due to compute limits
 */
export function batchSwapResults(
  swappable: JupiterQuoteResult[],
  maxPerBatch: number = 1
): JupiterQuoteResult[][] {
  const batches: JupiterQuoteResult[][] = [];

  for (let i = 0; i < swappable.length; i += maxPerBatch) {
    batches.push(swappable.slice(i, i + maxPerBatch));
  }

  return batches;
}

// ============================================================================
// LEGACY API IMPLEMENTATION (swap/v1)
// ============================================================================

/**
 * Legacy API Quote Request
 */
interface LegacyQuoteRequest {
  inputMint: string;
  outputMint: string;
  amount: number;
  slippageBps?: number;
  swapMode?: 'ExactIn' | 'ExactOut';
  instructionVersion?: string;
  onlyDirectRoutes?: string;
  maxAccounts?: string;
}

/**
 * Legacy API Swap Instructions Request
 *
 * Note: When output is native SOL, use nativeDestinationAccount.
 * When output is SPL token, use destinationTokenAccount.
 */
interface LegacySwapInstructionsRequest {
  userPublicKey: string;
  quoteResponse: JupiterQuoteResponse;
  wrapAndUnwrapSol?: boolean;
  dynamicComputeUnitLimit?: boolean;
  dynamicSlippage?: boolean;
  prioritizationFeeLamports?: {
    priorityLevelWithMaxLamports?: {
      priorityLevel: 'low' | 'medium' | 'high' | 'veryHigh';
      maxLamports: number;
    };
  };
  /** For SPL token destinations */
  destinationTokenAccount?: string;
  /** For native SOL destinations (use this when swapping to SOL) */
  nativeDestinationAccount?: string;
}

/**
 * Legacy API Swap Instructions Response
 */
interface LegacySwapInstructionsResponse {
  setupInstructions: Array<{
    programId: string;
    accounts: Array<{
      pubkey: string;
      isSigner: boolean;
      isWritable: boolean;
    }>;
    data: string;
  }>;
  swapInstruction: {
    programId: string;
    accounts: Array<{
      pubkey: string;
      isSigner: boolean;
      isWritable: boolean;
    }>;
    data: string;
  };
  cleanupInstruction?: {
    programId: string;
    accounts: Array<{
      pubkey: string;
      isSigner: boolean;
      isWritable: boolean;
    }>;
    data: string;
  };
  addressLookupTableAddresses: string[];
}

/**
 * Generic Legacy API request helper
 * Direct fetch() implementation - NO @jup-ag/api SDK
 */
async function jupiterLegacyApiRequest<T>(
  path: string,
  options: {
    method?: 'GET' | 'POST';
    body?: any;
    params?: Record<string, string | number>;
  } = {}
): Promise<T> {
  const { method = 'GET', body, params } = options;

  let url = `${JUPITER_LEGACY_API_URL}${path}`;
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value));
    });
    url += `?${searchParams.toString()}`;
  }

  const headers: Record<string, string> = {};
  const apiKey = getJupiterApiKey();
  if (apiKey) {
    headers['x-api-key'] = apiKey;
  }

  const requestOptions: RequestInit = {
    method,
    headers,
  };

  if (body && method === 'POST') {
    headers['Content-Type'] = 'application/json';
    requestOptions.body = JSON.stringify(body);
  }

  console.log(`[JUPITER-LEGACY] ${method} ${url}`);

  const response = await fetch(url, requestOptions);

  if (!response.ok) {
    // Throw the Response so handleJupiterError can process it
    throw response;
  }

  return response.json() as Promise<T>;
}

/**
 * Get quote from Legacy API (Step 1 of 2-step process)
 * GET /swap/v1/quote
 *
 * Note: instructionVersion V2 is required to support nativeDestinationAccount
 * in the subsequent swap-instructions request.
 */
async function getLegacyQuote(request: LegacyQuoteRequest): Promise<JupiterQuoteResponse> {
  return jupiterLegacyApiRequest<JupiterQuoteResponse>('/swap/v1/quote', {
    method: 'GET',
    params: {
      inputMint: request.inputMint,
      outputMint: request.outputMint,
      amount: request.amount,
      slippageBps: request.slippageBps ?? 500,
      swapMode: request.swapMode ?? 'ExactIn',
      instructionVersion: request.instructionVersion ?? 'V2',
      onlyDirectRoutes: request.onlyDirectRoutes ?? 'true',
      maxAccounts: request.maxAccounts ?? '20',
    },
  });
}

/**
 * Get swap instructions from Legacy API (Step 2 of 2-step process)
 * POST /swap/v1/swap-instructions
 */
async function getLegacySwapInstructions(
  request: LegacySwapInstructionsRequest
): Promise<LegacySwapInstructionsResponse> {
  return jupiterLegacyApiRequest<LegacySwapInstructionsResponse>('/swap/v1/swap-instructions', {
    method: 'POST',
    body: request,
  });
}

/**
 * Get Jupiter Legacy quotes for multiple tokens with swap instructions
 * 2-step process: quote → swap-instructions
 * NO auto-retry - returns categorized results for frontend to decide
 *
 * @param tokens - Array of tokens to get quotes for
 * @param userPublicKey - User's wallet address
 * @param receiver - Optional destination for SOL output (user_info PDA)
 * @returns Categorized results (swappable, retryable, nonSwappable)
 */
async function getLegacyQuotes(
  tokens: SwappableToken[],
  userPublicKey: string,
  receiver?: string
): Promise<SwapCategories> {
  console.log(`[JUPITER-LEGACY] Getting quotes for ${tokens.length} tokens`);
  console.log(`[JUPITER-LEGACY] User: ${userPublicKey}, Receiver: ${receiver || '(default)'}`);

  const results = await Promise.all(
    tokens.map(async (token): Promise<JupiterQuoteResult> => {
      try {
        // Step 1: Get quote
        const quote = await getLegacyQuote({
          inputMint: token.mint,
          outputMint: SOL_MINT,
          amount: token.balance,
          slippageBps: 500,
          swapMode: 'ExactIn',
        });

        // Step 2: Get swap instructions
        // With instructionVersion: V2 in the quote request, we can now use nativeDestinationAccount
        // to route SOL output to the user_info PDA for fee collection
        const swapIxResponse = await getLegacySwapInstructions({
          userPublicKey,
          quoteResponse: quote,
          wrapAndUnwrapSol: true,
          dynamicComputeUnitLimit: false, // false to reduce tx size
          dynamicSlippage: true,
          nativeDestinationAccount: receiver, // Route SOL to user_info PDA
          // NOTE: Priority fees removed to reduce transaction size (matches old working impl)
        });

        // Convert legacy response to JupiterSwapInstructionsResponse format
        // The legacy API returns instruction objects that need to be serialized
        const swapInstructions: JupiterSwapInstructionsResponse = {
          setupInstructions: swapIxResponse.setupInstructions,
          swapInstruction: swapIxResponse.swapInstruction,
          cleanupInstruction: swapIxResponse.cleanupInstruction,
          computeBudgetInstructions: [],
          otherInstructions: [],
          addressLookupTableAddresses: swapIxResponse.addressLookupTableAddresses,
          // Note: Legacy API does NOT use _ultraExtractedInstructions
          // serializeInstructions() will use the legacy path
        };

        return {
          mint: token.mint,
          tokenAccount: token.tokenAccount,
          balance: token.balance,
          decimals: token.decimals,
          symbol: token.symbol,
          quote,
          swapInstructions,
          addressLookupTableAddresses: swapIxResponse.addressLookupTableAddresses,
          canRetry: false,
        };
      } catch (error) {
        const { error: errorMessage, canRetry } = await handleJupiterError(error);

        console.warn(
          `[JUPITER-LEGACY] Failed to get quote for ${token.symbol} (${token.mint.slice(0, 8)}...): ${errorMessage}`
        );

        return {
          mint: token.mint,
          tokenAccount: token.tokenAccount,
          balance: token.balance,
          decimals: token.decimals,
          symbol: token.symbol,
          quote: null,
          swapInstructions: null,
          addressLookupTableAddresses: [],
          error: errorMessage,
          canRetry,
        };
      }
    })
  );

  // Categorize results - NO auto-retry
  const swappable: JupiterQuoteResult[] = [];
  const retryable: JupiterQuoteResult[] = [];
  const nonSwappable: JupiterQuoteResult[] = [];

  for (const result of results) {
    if (result.quote && result.swapInstructions) {
      swappable.push(result);
    } else if (result.canRetry) {
      retryable.push(result);
    } else {
      nonSwappable.push(result);
    }
  }

  console.log(
    `[JUPITER-LEGACY] Quote results: ${swappable.length} swappable, ${retryable.length} retryable, ${nonSwappable.length} non-swappable`
  );

  return { swappable, retryable, nonSwappable };
}

/**
 * Get Jupiter Legacy price quotes (quote only, no swap instructions)
 * Lighter-weight operation for displaying prices in the UI
 * NO auto-retry - returns results with canRetry flag
 *
 * @param tokens - Array of tokens to get quotes for
 * @returns Array of TokenPriceQuote with price information
 */
async function getLegacyPriceQuotes(tokens: SwappableToken[]): Promise<TokenPriceQuote[]> {
  console.log(`[JUPITER-LEGACY-PRICE] Getting price quotes for ${tokens.length} tokens`);

  const results = await Promise.all(
    tokens.map(async (token): Promise<TokenPriceQuote> => {
      try {
        // Get quote only (no swap instructions needed for price check)
        const quote = await getLegacyQuote({
          inputMint: token.mint,
          outputMint: SOL_MINT,
          amount: token.balance,
          slippageBps: 500,
          swapMode: 'ExactIn',
        });

        // Calculate estimated SOL value
        const lamports = parseInt(quote.outAmount, 10);
        const estimatedSolValue = lamports / 1e9;

        // Extract route info from quote
        const routeInfo = quote.routePlan?.length > 0
          ? {
              path: [quote.inputMint, ...quote.routePlan.map(r => r.swapInfo.outputMint)],
              dexes: quote.routePlan.map(r => r.swapInfo.label || 'Unknown'),
              priceImpact: parseFloat(quote.priceImpactPct) || 0,
            }
          : undefined;

        const meetsMinimum = estimatedSolValue >= 0.002; // MIN_SWAP_VALUE_SOL
        return {
          mint: token.mint,
          symbol: token.symbol,
          balance: token.balance,
          decimals: token.decimals,
          tokenAccount: token.tokenAccount,
          quote,
          estimatedSolValue,
          hasSwapRoute: true,
          meetsMinimum,
          isSwappable: meetsMinimum,
          canRetry: false,
          routeInfo,
        };
      } catch (error) {
        const { error: errorMessage, canRetry } = await handleJupiterError(error);

        console.warn(
          `[JUPITER-LEGACY-PRICE] Failed to get quote for ${token.symbol} (${token.mint.slice(0, 8)}...): ${errorMessage}`
        );

        return {
          mint: token.mint,
          symbol: token.symbol,
          balance: token.balance,
          decimals: token.decimals,
          tokenAccount: token.tokenAccount,
          quote: null,
          estimatedSolValue: 0,
          hasSwapRoute: false,
          meetsMinimum: false,
          isSwappable: false,
          error: errorMessage,
          canRetry,
        };
      }
    })
  );

  const swappableCount = results.filter(r => r.isSwappable).length;
  console.log(
    `[JUPITER-LEGACY-PRICE] Price quotes: ${swappableCount} swappable, ${results.length - swappableCount} not swappable`
  );

  return results;
}

// ============================================================================
// END LEGACY API IMPLEMENTATION
// ============================================================================

/**
 * Check if dust swapping is enabled
 * Uses config feature flag (enabled on mainnet, disabled on devnet)
 */
export function isDustSwapEnabled(): boolean {
  return (config as any).ENABLE_DUST_SWAP === true;
}

/**
 * Check if the app is running on mainnet
 * Dust swapping only works on mainnet
 */
export function isMainnet(): boolean {
  // First check the feature flag
  if (!isDustSwapEnabled()) {
    return false;
  }
  // Also check RPC URL as backup
  const rpcUrl = config.SOLANA_RPC_URL || '';
  return !rpcUrl.includes('devnet');
}

/**
 * Calculate estimated SOL output from swap results
 */
export function calculateEstimatedSolOutput(results: JupiterQuoteResult[]): number {
  return results.reduce((total, result) => {
    if (result.quote) {
      // outAmount is in lamports
      const lamports = parseInt(result.quote.outAmount, 10);
      return total + (lamports / 1e9);
    }
    return total;
  }, 0);
}

/**
 * Quote-only result for price checking (lighter than full JupiterQuoteResult)
 */
export interface TokenPriceQuote {
  mint: string;
  symbol: string;
  balance: number;
  decimals: number;
  tokenAccount: string;
  quote: JupiterQuoteResponse | null;
  estimatedSolValue: number;
  /** Whether Jupiter found a swap route for this token (regardless of value) */
  hasSwapRoute: boolean;
  /** Whether the token meets the minimum value threshold (0.002 SOL) */
  meetsMinimum: boolean;
  /** @deprecated Use hasSwapRoute && meetsMinimum instead. Kept for backward compatibility */
  isSwappable: boolean;
  error?: string;
  canRetry: boolean;
  routeInfo?: {
    path: string[];
    dexes: string[];
    priceImpact: number;
  };
}

/**
 * Generate mock price quotes for devnet testing
 * Simulates Jupiter responses with random but realistic values
 */
function getMockPriceQuotes(tokens: SwappableToken[]): TokenPriceQuote[] {
  console.log(`[JUPITER-PRICE-MOCK] Generating mock quotes for ${tokens.length} tokens (devnet mode)`);

  return tokens.map(token => {
    // Generate a pseudo-random but deterministic value based on token mint
    // This ensures the same token always gets the same mock value
    const hashCode = token.mint.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);

    // Generate mock SOL value between 0.001 and 0.1 SOL
    // Some tokens will be below the 0.002 threshold to test filtering
    const randomFactor = Math.abs(hashCode % 100) / 1000;
    const estimatedSolValue = 0.001 + randomFactor;

    // Generate mock route info
    const dexOptions = ['Raydium', 'Orca', 'Meteora', 'Phoenix', 'Lifinity'];
    const dexIndex = Math.abs(hashCode) % dexOptions.length;

    const mockQuote: JupiterQuoteResponse = {
      inputMint: token.mint,
      inAmount: token.balance.toString(),
      outputMint: SOL_MINT,
      outAmount: Math.floor(estimatedSolValue * 1e9).toString(),
      otherAmountThreshold: Math.floor(estimatedSolValue * 0.95 * 1e9).toString(),
      swapMode: 'ExactIn',
      slippageBps: 500,
      priceImpactPct: (Math.random() * 2).toFixed(2),
      routePlan: [
        {
          swapInfo: {
            ammKey: 'mock-amm-key',
            label: dexOptions[dexIndex],
            inputMint: token.mint,
            outputMint: SOL_MINT,
            inAmount: token.balance.toString(),
            outAmount: Math.floor(estimatedSolValue * 1e9).toString(),
            feeAmount: '0',
            feeMint: SOL_MINT,
          },
          percent: 100,
        },
      ],
    };

    const meetsMinimum = estimatedSolValue >= 0.002; // MIN_SWAP_VALUE_SOL
    return {
      mint: token.mint,
      symbol: token.symbol,
      balance: token.balance,
      decimals: token.decimals,
      tokenAccount: token.tokenAccount,
      quote: mockQuote,
      estimatedSolValue,
      hasSwapRoute: true, // Mock always has route
      meetsMinimum,
      isSwappable: meetsMinimum,
      canRetry: false,
      routeInfo: {
        path: [token.mint, SOL_MINT],
        dexes: [dexOptions[dexIndex]],
        priceImpact: parseFloat(mockQuote.priceImpactPct),
      },
    };
  });
}

/**
 * Get Jupiter quotes for price checking only (no swap instructions)
 * This is a lighter-weight operation for displaying prices in the UI
 *
 * Supports both Ultra API (default) and Legacy API.
 * On devnet, returns mock data since Jupiter doesn't support devnet.
 *
 * @param tokens - Array of tokens to get quotes for
 * @returns Array of TokenPriceQuote with price information
 */
export async function getJupiterPriceQuotes(
  tokens: SwappableToken[]
): Promise<TokenPriceQuote[]> {
  const apiVersion = getJupiterApiVersion();
  console.log(`[JUPITER-PRICE] getJupiterPriceQuotes called with ${tokens.length} tokens`);
  console.log(`[JUPITER-PRICE] API version: ${apiVersion.toUpperCase()}`);
  console.log(`[JUPITER-PRICE] Token mints:`, tokens.map(t => `${t.symbol}: ${t.mint}`));
  console.log(`[JUPITER-PRICE] isMainnet() = ${isMainnet()}, isDustSwapEnabled() = ${isDustSwapEnabled()}`);

  // Use mock data on devnet since Jupiter doesn't support it
  if (!isMainnet()) {
    console.log(`[JUPITER-PRICE] Not on mainnet, returning mock data`);
    return getMockPriceQuotes(tokens);
  }

  // Use Legacy API if configured
  if (apiVersion === 'legacy') {
    console.log(`[JUPITER-PRICE] On mainnet, fetching real Jupiter Legacy quotes...`);
    return getLegacyPriceQuotes(tokens);
  }

  // Ultra API (default)
  console.log(`[JUPITER-PRICE] On mainnet, fetching real Jupiter Ultra quotes...`);

  const results = await Promise.all(
    tokens.map(async (token): Promise<TokenPriceQuote> => {
      try {
        // Get Ultra order WITHOUT taker (price-only quote)
        const order = await getUltraOrder(token.mint, token.balance);

        // Calculate estimated SOL value
        const lamports = parseInt(order.outAmount, 10);
        const estimatedSolValue = lamports / 1e9;

        // Extract route info from order
        const routeInfo = order.routePlan?.length > 0
          ? {
              path: [order.inputMint, ...order.routePlan.map((r: { swapInfo: { outputMint: string } }) => r.swapInfo.outputMint)],
              dexes: order.routePlan.map((r: { swapInfo: { label?: string } }) => r.swapInfo.label || 'Unknown'),
              priceImpact: parseFloat(order.priceImpactPct) || 0,
            }
          : undefined;

        // Build quote response for compatibility
        const quote: JupiterQuoteResponse = {
          inputMint: order.inputMint,
          inAmount: order.inAmount,
          outputMint: order.outputMint,
          outAmount: order.outAmount,
          otherAmountThreshold: order.otherAmountThreshold,
          swapMode: order.swapMode,
          slippageBps: order.slippageBps,
          priceImpactPct: order.priceImpactPct,
          routePlan: order.routePlan,
          contextSlot: order.contextSlot,
          timeTaken: order.timeTaken,
        };

        const meetsMinimum = estimatedSolValue >= 0.002; // MIN_SWAP_VALUE_SOL
        return {
          mint: token.mint,
          symbol: token.symbol,
          balance: token.balance,
          decimals: token.decimals,
          tokenAccount: token.tokenAccount,
          quote,
          estimatedSolValue,
          hasSwapRoute: true,
          meetsMinimum,
          isSwappable: meetsMinimum,
          canRetry: false,
          routeInfo,
        };
      } catch (error) {
        const { error: errorMessage, canRetry } = await handleJupiterError(error);

        console.warn(
          `[JUPITER-PRICE] Failed to get quote for ${token.symbol} (${token.mint.slice(0, 8)}...): ${errorMessage}`
        );

        return {
          mint: token.mint,
          symbol: token.symbol,
          balance: token.balance,
          decimals: token.decimals,
          tokenAccount: token.tokenAccount,
          quote: null,
          estimatedSolValue: 0,
          hasSwapRoute: false,
          meetsMinimum: false,
          isSwappable: false,
          error: errorMessage,
          canRetry,
        };
      }
    })
  );

  const swappableCount = results.filter(r => r.isSwappable).length;
  console.log(
    `[JUPITER-PRICE] Price quotes: ${swappableCount} swappable, ${results.length - swappableCount} not swappable`
  );

  return results;
}
